// apps/client/src/services/selfInvoices.ts
//
// Frontend service for the self-referencing invoice journals feature.
// Lists Xero contacts (for the dropdown) and persists the org-level config.

import { apiClient } from '.';

export interface XeroContactSummary {
  id: string;
  name: string;
  isCustomer?: boolean;
  isSupplier?: boolean;
}

/**
 * Fetch a list of Xero contacts for the dropdown. Server returns up to 5 pages
 * by default (500 contacts). Pass `search` to filter server-side.
 */
export async function listXeroContacts(
  organisationId: string,
  search?: string
): Promise<XeroContactSummary[]> {
  const params = new URLSearchParams();
  if (search && search.trim().length > 0) params.append('search', search.trim());
  const qs = params.toString();
  const url = `/organisations/${organisationId}/xero/contacts${qs ? `?${qs}` : ''}`;

  const resp = await apiClient.get(url);
  if (!resp.ok) {
    throw new Error(`Failed to list Xero contacts (${resp.status})`);
  }
  const data = await resp.json();
  return (data.contacts ?? []) as XeroContactSummary[];
}

export interface SelfInvoiceConfig {
  enabled: boolean;
  contactId: string | null;
}

/**
 * Save the org-level self-invoice config. The server enforces that enabling
 * requires a contactId.
 */
export async function updateSelfInvoiceConfig(
  organisationId: string,
  config: SelfInvoiceConfig
): Promise<{ selfInvoicesEnabled: boolean; selfXeroContactId: string | null }> {
  const resp = await apiClient.patch(
    `/organisations/${organisationId}/xero/self-invoices/config`,
    config
  );
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(text || `Failed to update self-invoice config (${resp.status})`);
  }
  const data = await resp.json();
  return {
    selfInvoicesEnabled: !!data.selfInvoicesEnabled,
    selfXeroContactId: data.selfXeroContactId ?? null,
  };
}
